package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences
import diana.soleil.design.iraniancanadienbazzar.core.common.deviceRegionCode
import diana.soleil.design.iraniancanadienbazzar.core.model.Country
import diana.soleil.design.iraniancanadienbazzar.core.model.Metros
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Holds the active **country** and **metro/region** as observable state, both **persisted** via
 * [AppPreferences] so they survive restarts. Country is a first-class filter dimension
 * (docs/US_EXPANSION_PLAN.md): a selected metro implies its country, and `null` metro means
 * "All \<active country\>". Repository-backed screens scope their queries to the pair; the city selector
 * and onboarding update them.
 *
 * Also holds the set of **launched** metro ids ([launchedMetros]) — the cities that are actually live
 * for rollout, across both countries. This is driven by Remote Config's `active_launch_cities` from the
 * composition root (so feature modules don't depend on `:core:firebase`); the city selector shows the
 * rest as "coming soon" and refuses to select them. Signed-in accounts mirror the choice to
 * `users/{uid}` via `UserPreferencesSync` (`:shared`). Lives in `:core:data` because feature ViewModels
 * scope by it.
 */
class CityController(private val preferences: AppPreferences) {
    private val _activeMetroId = MutableStateFlow(preferences.metroId)
    val activeMetroId: StateFlow<String?> = _activeMetroId.asStateFlow()

    // Country is derived on init: a persisted metro wins (it implies its country); else the persisted
    // country choice; else the device region; else the home market (CA).
    private val _activeCountry = MutableStateFlow(resolveInitialCountry())
    val activeCountry: StateFlow<Country> = _activeCountry.asStateFlow()

    // Mirrors the Remote Config default (the seeded metros) so gating is sane before the first fetch.
    private val _launchedMetros = MutableStateFlow(listOf("gta", "mtl", "yvr"))
    val launchedMetros: StateFlow<List<String>> = _launchedMetros.asStateFlow()

    private fun resolveInitialCountry(): Country {
        val fromMetro = Metros.byId(preferences.metroId)?.country
        if (fromMetro != null) return fromMetro
        Country.fromCode(preferences.countryCode)?.let { return it }
        return Country.fromRegion(deviceRegionCode())
    }

    /**
     * Select the active country. Persisted; if the current metro doesn't belong to [country] it falls
     * back to "All \<country\>" (metro cleared). No-op semantics on the metro when it already matches.
     */
    fun setCountry(country: Country) {
        preferences.countryCode = country.code
        _activeCountry.value = country
        val metro = Metros.byId(_activeMetroId.value)
        if (metro != null && metro.country != country) {
            preferences.metroId = null
            _activeMetroId.value = null
        }
    }

    /**
     * Select a metro; ignored when the metro isn't launched (null = "All \<country\>" is always
     * allowed). A non-null metro also sets the active country to that metro's country.
     */
    fun setMetro(metroId: String?) {
        if (metroId != null && !isLaunched(metroId)) return
        preferences.metroId = metroId
        _activeMetroId.value = metroId
        Metros.byId(metroId)?.let { metro ->
            if (metro.country != _activeCountry.value) {
                preferences.countryCode = metro.country.code
                _activeCountry.value = metro.country
            }
        }
    }

    /** Pushed from the composition root after a Remote Config fetch. */
    fun setLaunchedMetros(metroIds: List<String>) {
        if (metroIds.isEmpty()) return
        _launchedMetros.value = metroIds
        // If the persisted active metro is no longer launched, fall back to "All <country>".
        val active = _activeMetroId.value
        if (active != null && !isLaunched(active)) {
            preferences.metroId = null
            _activeMetroId.value = null
        }
        // If the active COUNTRY no longer has any launched metro (e.g. US turned off for a CA-only
        // launch), snap back to a launched country — otherwise a user persisted on US is stranded on
        // an all-"coming soon" list with the country toggle now hidden.
        val launchedCountries = Metros.launchedCountries(metroIds)
        if (_activeCountry.value !in launchedCountries) {
            val fallback = launchedCountries.first()
            preferences.countryCode = fallback.code
            _activeCountry.value = fallback
            preferences.metroId = null
            _activeMetroId.value = null
        }
    }

    fun isLaunched(metroId: String): Boolean =
        _launchedMetros.value.any { it.equals(metroId, ignoreCase = true) }
}
